import { ActorRefFrom, createMachine, sendParent } from 'xstate'

import { events as adAssetsDashboardEvents } from '../FileUploadersMachine/constants'

import { states, events, LOADING, FAILED, SUCCESS } from './constants'

export interface FileUploaderContextTypes {
  selectedFileDetails: any
  configuredAWSS3Client: any
  configDetails: any
  isFailedToUpload: boolean
  s3UploadedFileInfo: any
  deleteFromS3: boolean
  extraBucketPath?: string
  getCustomFileName: Function
  fileUploadDetails: any
}

export type FileUploaderEvent =
  | { type: 'CHOOSE_FILE_CLICKED' }
  | { type: 'CANCELLED_TO_CHOOSE_FILE' }
  | {
      type: 'UPLOAD_FILE_CLICKED'
      imgDetails: any
      overrideBucketPath?: string
      getCustomFileName?: any
    }
  | { type: 'DELETE_FILE' }
  | { type: 'UPLOAD_TO_AWS' }
  | { type: 'GO_TO_SUCCESS_STATE' }

export const fileUploaderMachine = (id: string) =>
  createMachine<FileUploaderContextTypes, FileUploaderEvent>({
    id: id,
    initial: states.initial,
    on: {
      [events.updateS3ClientAndConfigDetails]: [
        {
          actions: ['updateS3ClientAndConfigDetails'],
          cond: 'isFailedToUpload',
          target: states.uploadToAWS
        },
        {
          target: states.initial
        }
      ]
    },
    states: {
      [states.initial]: {
        on: {
          [events.chooseFileClicked]: states.chooseFileClicked,
          [events.gotoSuccessStep]: {
            actions: ['assignExistingFileUploaderValuesToContext'],
            target: states.successfullyUploadedState
          },
          [events.uploadFileClicked]: [
            {
              cond: 'isValidFileExtension',
              actions: 'assignSelectedFileDetails',
              target: states.proceedToUpload
            },
            {
              cond: 'isNotValidFileExtension',
              actions: ['showInvalidFileExtensionToast'],
              target: states.initial
            },
            {
              target: states.initial
            }
          ]
        }
      },
      [states.chooseFileClicked]: {
        on: {
          [events.cancelledToChooseFile]: states.initial,
          [events.uploadFileClicked]: [
            {
              cond: 'isValidFileExtension',
              actions: ['assignSelectedFileDetails'],
              target: states.proceedToUpload
            },
            {
              cond: 'isNotValidFileExtension',
              actions: ['showInvalidFileExtensionToast'],
              target: states.initial
            },
            {
              target: states.initial
            }
          ]
        }
      },
      [states.proceedToUpload]: {
        on: {
          '': states.uploadToAWS
        }
      },
      [states.uploadToAWS]: {
        tags: [LOADING],
        invoke: {
          src: 'uploadToAWS',
          onDone: {
            actions: 'updateUploadedFileInfo',
            target: states.uploadToBackend
          },
          onError: [
            {
              actions: [
                'errorInfo',
                sendParent((context, event) => ({
                  type: adAssetsDashboardEvents.getAWSCredentials,
                  data: event.data
                }))
              ],
              cond: 'isExpiredToken',
              target: states.getUpdatedS3Credentials
            },
            {
              actions: 'showErrorToast',
              target: states.failedToUploadToAWS
            }
          ]
        },
        on: {
          [events.deleteFile]: {
            target: states.abortUploadToAWS
          }
        }
      },
      [states.abortUploadToAWS]: {
        invoke: {
          src: 'abortUploadingToAWS',
          onDone: {
            actions: 'clearSelectedFileDetails',
            target: states.initial
          },
          onError: states.abortUploadToAWS
        }
      },
      [states.uploadToBackend]: {
        tags: [LOADING],
        entry: ['removeFailedToUploadState'],
        invoke: {
          src: 'uploadToBackend',
          onDone: states.successfullyUploadedState,
          onError: states.failedToUploadToBackend
        }
      },
      [states.successfullyUploadedState]: {
        tags: [SUCCESS],
        on: {
          [events.deleteFile]: {
            actions: 'clearSelectedFileDetails',
            target: states.initial
          },
          [events.chooseFileClicked]: states.chooseFileClicked,
          [events.uploadFileClicked]: [
            {
              cond: 'isValidFileExtension',
              actions: 'assignSelectedFileDetails',
              target: states.proceedToUpload
            },
            {
              cond: 'isNotValidFileExtension',
              actions: ['showInvalidFileExtensionToast'],
              target: states.initial
            },
            {
              target: states.initial
            }
          ]
        }
      },
      [states.deleteFile]: {
        entry: 'clearSelectedFileDetails',
        invoke: {
          src: 'removeFile',
          onDone: [{ target: states.initial, actions: 'onDeleteSuccess' }],
          onError: states.successfullyUploadedState
        }
      },
      [states.deleteFromS3Bucket]: {
        entry: 'clearSelectedFileDetails',
        invoke: {
          src: 'deleteFromS3Bucket',
          onDone: states.initial,
          onError: states.successfullyUploadedState
        }
      },
      [states.getUpdatedS3Credentials]: {
        tags: [LOADING],
        on: {
          [events.updateS3ClientAndConfigDetails]: [
            {
              actions: ['updateS3ClientAndConfigDetails'],
              cond: 'isFailedToUpload',
              target: states.uploadToAWS
            },
            {
              target: states.initial
            }
          ]
        }
      },
      [states.failedToUploadToBackend]: {
        on: {
          [events.uploadToBackend]: states.uploadToBackend
        }
      },
      [states.failedToUploadToAWS]: {
        tags: [FAILED],
        on: {
          [events.uploadToAWS]: states.uploadToAWS,
          [events.deleteFile]: {
            actions: 'clearSelectedFileDetails',
            target: states.initial
          }
        }
      }
    }
  })

export type FileUploaderMachineActor = ActorRefFrom<
  ReturnType<typeof fileUploaderMachine>
>
